import type { TranslationTree } from '../../types';

export const toolRecommender: TranslationTree = {
  toolRecommender: {
    page: {
      breadcrumb: "Éco-conception d'outil",
      badge: 'Optimiseur par algorithme génétique',
      title: "Trouver l'outil le plus adapté à votre contexte d'usinage",
      description:
        "Configurez vos contraintes d'usinage et lancez l'algorithme génétique. Le modèle évalue les outils candidats et les classe par performance prédite.",
    },
    tabs: {
      generate: 'Générer',
      history: 'Historique',
    },
    form: {
      sectionTitle: 'Configuration',
      descriptionLabel: "Description de l'essai (optionnel)",
      descriptionPlaceholder: 'ex. Finition aluminium',
      materialsLabel: 'Matériaux',
      operationsLabel: "Opérations d'usinage",
      nJobsLabel: 'Threads parallèles (nJobs)',
      topNLabel: 'Résultats (topN)',
      baseToolLabel: 'Outil de base (optionnel)',
      baseToolPlaceholder: '{"d1": 1, "Nb dents": 3, ...}',
      runButton: "Lancer l'AG",
    },
    running: {
      title: 'AG en cours…',
      hint: "Cela peut prendre plusieurs minutes (jusqu'à 5). Veuillez patienter.",
      resumedHint:
        "Vous êtes revenu sur cette page — l'expérience tourne toujours en arrière-plan.",
    },
    results: {
      redirect: 'Expérience terminée — ouverture des résultats…',
    },
    timeout: {
      title: 'AG toujours en cours',
      message:
        "L'algorithme a dépassé le délai de réponse. Les résultats apparaîtront dans l'historique une fois terminés.",
      historyHint:
        "Accédez à l'onglet Historique et rafraîchissez pour voir le résultat dès qu'il est disponible.",
    },
    idle: {
      title: 'Aucun résultat',
      description: "Configurez les contraintes et lancez l'AG pour voir les outils classés.",
    },
    error: {
      title: "Échec de l'inférence",
    },
    history: {
      sectionTitle: 'Expériences passées',
      empty: "Aucune expérience. Lancez l'AG pour voir les résultats ici.",
      colDate: 'Date',
      colDescription: 'Description',
      colId: 'ID expérience',
      colResults: 'Résultats',
      viewLink: 'Voir',
      refresh: "Actualiser l'historique",
      results: '{{n}} résultats',
    },
    detail: {
      breadcrumb: 'Expérience',
      noResults: 'Aucun résultat stocké pour cette expérience.',
      resultLabel: 'Résultat',
      baseScoreLabel: "Score de l'outil de base",
      generatedScoreLabel: "Score de l'outil généré",
      baseFromCatalog: 'Outil de base : article du catalogue',
      baseFromUser: 'Outil de base : votre outil',
      noBaseTool: 'Aucun outil de base disponible pour la comparaison.',
      colField: 'Champ',
      colBase: 'Outil de base',
      colGenerated: 'Outil généré',
      advancedTitle: "Avancé — {{n}} autres champs de l'outil de base",
      exportXlsx: 'Exporter en Excel',
      exportRank: 'Rang',
      exportMaterial: 'Matière',
      exportOperation: 'Opération',
      exportBaseToolArticle: "Article de l'outil de base",
    },
    baseTool: {
      sheetTitle: 'Outil de base',
      sheetDescription:
        "Décrivez l'outil à optimiser. Le formulaire part de l'outil le plus courant du catalogue ; tous les champs sont obligatoires, et l'onglet JSON permet de coller un outil existant.",
      tabForm: 'Formulaire',
      tabJson: 'JSON',
      filled: '{{filled}} / {{total}} champs renseignés',
      errors: '{{n}} valeur(s) invalide(s)',
      choose: 'Choisir…',
      range: '{{min}} à {{max}} {{unit}}',
      rangeExclusive: '> {{min}} à {{max}} {{unit}}',
      clear: 'Tout effacer',
      restoreDefaults: 'Valeurs par défaut',
      apply: 'Utiliser cet outil',
      jsonLabel: "JSON de l'outil — les clés sont les alias ISO 13399",
      groups: {
        dimensions: 'Dimensions',
        cutting: 'Coupe',
        angles: 'Angles et dépouilles',
        grinding: 'Affûtage, noyau et goujures',
        material: "Matière de l'outil",
      },
      issues: {
        outOfRange: 'Hors plage : {{range}}',
        notANumber: 'Nombre attendu',
        unknownChoice: 'Valeur non reconnue',
      },
      json: {
        syntax: 'JSON invalide : {{message}}',
        notAnObject: 'Un objet JSON est attendu, par exemple {"d1": 3}',
        unknownKeys: 'Clés inconnues du modèle, elles seront ignorées : {{keys}}',
      },
      summary: {
        none: 'Aucun outil de base',
        noneHint: "L'algorithme partira des meilleurs outils du catalogue.",
        line: 'Ø{{d1}} · {{teeth}} dents · hélice {{helix}}° · {{substrate}}',
        setHint: "Sert de point de départ à l'algorithme.",
        define: 'Définir un outil…',
        edit: 'Modifier…',
        remove: 'Retirer',
      },
    },
    fields: {
      d1: 'Diamètre de coupe (d1)',
      l1: 'Longueur de coupe (l1)',
      nbDents: 'Nombre de dents',
      helice1: 'Hélice 1 [°]',
      helice2: 'Hélice 2 [°]',
      sensDeCoupe: 'Sens de coupe',
      profil: 'Profil',
      pasDuProfil: 'Pas du profil',
      formeFraise: 'Forme de la fraise',
      coin: 'Coin',
      valeurCoin: 'Valeur du coin',
      coupeCentre: 'Coupe au centre',
      casseCoupe: 'Cassé de coupe',
      largeurCasseCoupe: 'Largeur du cassé de coupe',
      angleCoupeBout: 'Angle de coupe en bout [°]',
      angleCoupeDiametreMin: 'Angle de coupe au diamètre mini (goujure 1) [°]',
      angleCoupeDiametreMax: 'Angle de coupe au diamètre maxi (goujure 1) [°]',
      depouilleBout1: '1ère dépouille en bout [°]',
      depouilleBout2: '2ème dépouille en bout [°]',
      depouilleDiam1: '1ère dépouille au diamètre [°]',
      depouilleDiam2: '2ème dépouille au diamètre [°]',
      depouilleDiamType: 'Type de dépouille au diamètre',
      affutageBout: 'Affûtage en bout',
      largeurAffutageBout: "Largeur d'affûtage en bout",
      depassementCentre: 'Dépassement du centre',
      largeurListel: 'Largeur du listel',
      largeurAme: "Largeur d'âme",
      noyauMm: 'Noyau [mm]',
      noyauArriere: 'Noyau arrière',
      creux1: 'Creux 1 [°]',
      creux2: 'Creux 2 [°]',
      creux3: 'Creux 3 [°]',
      longueurCreux: 'Longueur du creux',
      indexTaillage: 'Indexation de taillage',
      indexTaillageAngle: "Angle d'indexation de taillage",
      rayonRectifiage: 'Rayon de rectifiage',
      longueurUtileMax: 'Longueur utile maximum LUX [mm]',
      demiAngleCone: 'Demi-angle du cône du corps BHTA [°]',
      diamCollet: 'Diamètre du collet n°1 (D2)',
      longCollet: 'Longueur du collet n°1 (L2)',
      fabricationType: 'Type de fabrication',
      substrat: "Substrat / matière de l'outil",
      percentCo: 'Teneur en cobalt [%]',
      tailleGrain: 'Taille de grain',
      reference: 'Référence',
      article: 'Article',
    },
  },
};
